import { Link, useNavigate } from 'react-router-dom'
import {
  CalendarDays,
  ClipboardList,
  Eye,
  FileText,
  Lock,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Trash2,
  Unlock,
  WalletCards,
} from 'lucide-react'
import type { UseMutationResult } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { WorkflowStatusSelect } from '@/components/jobs/workflow-status-select'
import { canLockJob, canUnlockJob } from '@/lib/job-lock'
import {
  getJobStatusChangeBlockedMessage,
  isJobModificationBlocked,
} from '@/lib/job-status'
import { withServiceInstanceId } from '@/lib/service-instance'
import { withReturnTo } from '@/lib/return-navigation'
import type { Job } from '@/types'

type JobListActionsMenuProps = {
  job: Job
  serviceInstanceId?: string | null
  assignmentReturnPath?: string | null
  onView: () => void
  onChat?: () => void
  onDelete: () => void
  onOrderSelect?: (orderReferenceId: string) => void
  onStatusApplied?: () => void
  lockMutation: UseMutationResult<unknown, unknown, { id: string; lock: boolean }, unknown>
  triggerClassName?: string
  align?: 'start' | 'center' | 'end'
}

export function JobListActionsMenu({
  job,
  serviceInstanceId,
  assignmentReturnPath,
  onView,
  onChat,
  onDelete,
  onOrderSelect,
  onStatusApplied,
  lockMutation,
  triggerClassName,
  align = 'end',
}: JobListActionsMenuProps) {
  const navigate = useNavigate()
  const jobModificationBlocked = isJobModificationBlocked(job)
  const jobEditDisabled = Boolean(job.has_invoice) || jobModificationBlocked
  const jobStatusChangeDisabled = jobEditDisabled
  const jobStatusChangeBlockedMessage = getJobStatusChangeBlockedMessage(job)
  const jobWorkflowStatusLabel = job.workflow_status_label || 'Workflow not set'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Actions for job ${job.job_ref || job.id}`}
          className={triggerClassName}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="w-48 text-sm"
        onClick={(event) => event.stopPropagation()}
      >
        <DropdownMenuItem onClick={(event) => {
          event.stopPropagation()
          onView()
        }}>
          <Eye className="mr-2 h-4 w-4" />
          View
        </DropdownMenuItem>
        {onChat ? (
          <DropdownMenuItem onClick={(event) => {
            event.stopPropagation()
            onChat()
          }}>
            <MessageCircle className="mr-2 h-4 w-4" />
            Chat
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onClick={(event) => {
          event.stopPropagation()
          navigate(withReturnTo(withServiceInstanceId(`/finance/invoices/${job.id}`, serviceInstanceId), assignmentReturnPath))
        }}>
          <FileText className="mr-2 h-4 w-4" />
          Invoice preview
        </DropdownMenuItem>
        <DropdownMenuItem asChild onClick={(event) => event.stopPropagation()}>
          <Link to={withReturnTo(withServiceInstanceId(`/workdays?job=${encodeURIComponent(String(job.id))}`, serviceInstanceId), assignmentReturnPath)}>
            <CalendarDays className="mr-2 h-4 w-4" />
            Work Days
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild onClick={(event) => event.stopPropagation()}>
          <Link to={withReturnTo(withServiceInstanceId(`/assignments?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId), assignmentReturnPath)}>
            <ClipboardList className="mr-2 h-4 w-4" />
            Assignments
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild onClick={(event) => event.stopPropagation()}>
          <Link to={withReturnTo(withServiceInstanceId(`/job/worker/payments?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId), assignmentReturnPath)}>
            <WalletCards className="mr-2 h-4 w-4" />
            Worker payouts
          </Link>
        </DropdownMenuItem>
        {job.order_reference_id && onOrderSelect ? (
          <DropdownMenuItem onClick={(event) => {
            event.stopPropagation()
            onOrderSelect(job.order_reference_id!)
          }}>
            <FileText className="mr-2 h-4 w-4" />
            View order
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation()
            navigate(withServiceInstanceId(`/job/${job.id}?edit=true`, serviceInstanceId))
          }}
          disabled={jobEditDisabled}
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5">
          <span className="mb-1 block text-xs text-muted-foreground">Change status</span>
          <WorkflowStatusSelect
            entityType="job"
            entityId={job.id}
            currentValue={jobWorkflowStatusLabel}
            disabled={jobStatusChangeDisabled}
            className="h-8 w-full border-muted-foreground/20 bg-muted/40 text-xs"
            onApplied={onStatusApplied}
          />
          {jobStatusChangeBlockedMessage ? (
            <p className="mt-1 text-[11px] text-muted-foreground">
              {jobStatusChangeBlockedMessage}
            </p>
          ) : null}
        </div>
        <DropdownMenuSeparator />
        {job.is_locked && canUnlockJob(job) ? (
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation()
              lockMutation.mutate({ id: job.id, lock: false })
            }}
          >
            <Unlock className="mr-2 h-4 w-4" />
            Unlock
          </DropdownMenuItem>
        ) : null}
        {!job.is_locked && canLockJob(job) ? (
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation()
              lockMutation.mutate({ id: job.id, lock: true })
            }}
          >
            <Lock className="mr-2 h-4 w-4" />
            Lock
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation()
            onDelete()
          }}
          className="text-destructive"
          disabled={jobModificationBlocked}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
